/* eslint-disable array-callback-return */
import moment from "moment";
import { BASE_API_URL } from "../config";
import { isAuthenticated, setToken, clearToken, authFetch } from "./authHelper";

let currentRandomSeed = Math.floor(Math.random() * 1000000);

export function buildPagination(response: any) {
  if (!response || !response.items) return null;
  return {
    total: response.total,
    page: response.page,
    limit: response.limit,
    hasMore: (response.page * response.limit) < response.total,
  };
}

export const mainFunctions = {
  //
  parsePathParams(pathname = "") {
    const match = pathname.match(/^\/tier-list\/(albums|songs|artists|year)(?:\/(.+))?$/i);
    if (!match) return null;

    const rawType = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    const validListTypes = ["Albums", "Artists", "Year", "Songs"];
    const listType = validListTypes.includes(rawType) ? rawType : "Albums";
    const groupByValue = match[2] ? decodeURIComponent(match[2]) : undefined;

    const initialGroupByType = groupByValue ? listType : undefined;
    const defaultYear = listType === "Year" && groupByValue ? groupByValue : "2025";

    let tierListName = "";
    if (groupByValue && groupByValue !== "All") {
      if (listType === "Year") {
        tierListName = `Best Albums of ${groupByValue}`;
      } else if (listType === "Artists" && groupByValue.length === 1) {
        tierListName = `Artists: ${groupByValue}`;
      } else if (listType === "Songs") {
        tierListName = `${groupByValue} Songs`;
      } else {
        tierListName = groupByValue;
      }
    }

    return {
      listType,
      photoType: listType === "Artists" ? "Artists" : listType === "Songs" ? "Songs" : "Albums",
      initialGroupByType,
      initialGroupByValue: groupByValue,
      defaultYear,
      tierListName,
    };
  },

  parseQueryParams(search = "") {
    const params = new URLSearchParams(search);
    const rawListType = params.get("listType");
    const normalizedListType = rawListType
      ? `${rawListType.charAt(0).toUpperCase()}${rawListType.slice(1).toLowerCase()}`
      : undefined;

    const groupByValue = params.get("groupBy") || params.get("year") || params.get("letter");

    const validListTypes = ["Albums", "Artists", "Year", "Songs"];
    const listType = validListTypes.includes(normalizedListType) ? normalizedListType : "Albums";

    const initialGroupByType = groupByValue ? listType : undefined;
    const defaultYear = initialGroupByType === "Year" && groupByValue ? groupByValue : "2025";

    // Auto-generate a default title from the list type and groupBy value
    let tierListName = "";
    if (groupByValue && groupByValue !== "All") {
      if (listType === "Year") {
        tierListName = `Best Albums of ${groupByValue}`;
      } else if (listType === "Artists" && groupByValue.length === 1) {
        tierListName = `Artists: ${groupByValue}`;
      } else if (listType === "Songs") {
        tierListName = `${groupByValue} Songs`;
      } else {
        tierListName = groupByValue;
      }
    }

    // Parse custom tiers from URL (base64-encoded JSON)
    let parsedCustomTiers = null;
    const customTiersParam = params.get("customTiers");
    if (customTiersParam) {
      try {
        parsedCustomTiers = JSON.parse(atob(customTiersParam));
        if (!Array.isArray(parsedCustomTiers)) parsedCustomTiers = null;
      } catch {
        parsedCustomTiers = null;
      }
    }

    // Build initialTiers from custom tiers if provided
    let initialTiers: Record<string, any[]> | undefined;
    if (parsedCustomTiers && parsedCustomTiers.length > 0) {
      initialTiers = {};
      parsedCustomTiers.forEach((t: any) => {
        if (t.name) initialTiers![String(t.name)] = [];
      });
    }

    // Parse inspired-by capture ID
    const fromParam = params.get("from");
    const inspiredByCaptureId = fromParam && /^\d+$/.test(fromParam) ? parseInt(fromParam, 10) : null;

    return {
      listType,
      photoType: listType === "Artists" ? "Artists" : listType === "Songs" ? "Songs" : "Albums",
      initialGroupByType,
      initialGroupByValue: groupByValue || undefined,
      defaultYear,
      tierListName,
      ...(initialTiers ? { initialTiers } : {}),
      ...(parsedCustomTiers ? { initialTierColors: parsedCustomTiers } : {}),
      ...(inspiredByCaptureId ? { inspiredByCaptureId } : {}),
    };
  },

  async loadMusicData() {
    if (this.state.artistList.length > 0 && this.state.albumList.length > 0) return;
    const PAGE_SIZE = 100;

    const listType = this.state.listType;
    const initialArtist = this.state.initialGroupByValue;

    // Year mode: fetch all albums for that year directly (no pagination)
    if (listType === "Year") {
      const year = this.state.defaultYear || "2026";
      const [artistResponse, albumResponse] = await Promise.all([
        this.getCommands.getCommon(`getArtists?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`),
        this.getCommands.getCommon(`getAlbums?year=${encodeURIComponent(year)}`),
      ]);

      if (artistResponse && albumResponse) {
        const albums = albumResponse.items || albumResponse;
        this.setState({
          artistList: artistResponse.items || artistResponse,
          albumList: Array.isArray(albums) ? albums : [],
          albumPagination: null,
          artistPagination: buildPagination(artistResponse),
        });
      }
      return;
    }

    // If we have an initial artist filter for Albums/Songs, apply it
    const isFilterable = listType === "Albums" || listType === "Songs";
    const artistParam = (initialArtist && initialArtist !== "All" && isFilterable)
      ? `&artist=${encodeURIComponent(initialArtist)}`
      : "";
    const randomParam = !artistParam ? `&random=true&randomSeed=${currentRandomSeed}` : "";

    const [artistResponse, albumResponse] = await Promise.all([
      this.getCommands.getCommon(`getArtists?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`),
      this.getCommands.getCommon(`getAlbums?page=1&limit=${PAGE_SIZE}${artistParam}${randomParam}`),
    ]);

    if (artistResponse && albumResponse) {
      this.setState({
        artistList: artistResponse.items || artistResponse,
        albumList: albumResponse.items || albumResponse,
        albumPagination: buildPagination(albumResponse),
        artistPagination: buildPagination(artistResponse),
      });
    }
  },

  async loadCustomItems() {
    if (!isAuthenticated()) {
      this.setState({ customItems: { Artists: [], Albums: [], Songs: [] } });
      return;
    }

    const response = await this.getCommands.getCommon("customItems");
    if (!response || typeof response !== "object") return;

    this.setState({
      customItems: {
        Artists: response.Artists || [],
        Albums: response.Albums || [],
        Songs: response.Songs || [],
      },
    });
  },

  async loadMoreAlbums(artistFilter?: string) {
    const pagination = this.state.albumPagination;
    if (!pagination || !pagination.hasMore) return;

    const nextPage = pagination.page + 1;
    const artistParam = artistFilter ? `&artist=${encodeURIComponent(artistFilter)}` : "";
    const randomParam = !artistFilter ? `&random=true&randomSeed=${currentRandomSeed}` : "";
    const response = await this.getCommands.getCommon(
      `getAlbums?page=${nextPage}&limit=${pagination.limit}${artistParam}${randomParam}`
    );

    if (response?.items) {
      this.setState((prevState: any) => ({
        albumList: [...prevState.albumList, ...response.items],
        albumPagination: {
          ...pagination,
          page: nextPage,
          hasMore: (nextPage * pagination.limit) < response.total,
        },
      }));
    }
  },

  async loadMoreArtists() {
    const pagination = this.state.artistPagination;
    if (!pagination || !pagination.hasMore) return;

    const nextPage = pagination.page + 1;
    const response = await this.getCommands.getCommon(
      `getArtists?page=${nextPage}&limit=${pagination.limit}&random=true&randomSeed=${currentRandomSeed}`
    );

    if (response?.items) {
      this.setState((prevState: any) => ({
        artistList: [...prevState.artistList, ...response.items],
        artistPagination: {
          ...pagination,
          page: nextPage,
          hasMore: (nextPage * pagination.limit) < response.total,
        },
      }));
    }
  },

  async filterAlbumsByYear(year: string) {
    const response = await this.getCommands.getCommon(
      `getAlbums?year=${encodeURIComponent(year)}`
    );

    const albums = response?.items || response;
    if (Array.isArray(albums)) {
      this.setState({
        albumList: albums,
        albumPagination: null,
      });
    }
  },

  async filterAlbumsByArtist(artist: string | null) {
    const PAGE_SIZE = 100;
    const artistParam = artist ? `&artist=${encodeURIComponent(artist)}` : "";
    const response = await this.getCommands.getCommon(
      `getAlbums?page=1&limit=${PAGE_SIZE}${artistParam}`
    );

    if (response?.items) {
      this.setState({
        albumList: response.items,
        albumPagination: buildPagination(response),
      });
    }
  },

  async loadMoreSongs(artistFilter?: string) {
    const pagination = this.state.songPagination;
    if (!pagination || !pagination.hasMore) return;

    const nextPage = pagination.page + 1;
    const artistParam = artistFilter ? `&artist=${encodeURIComponent(artistFilter)}` : "";
    const randomParam = !artistFilter ? `&random=true&randomSeed=${currentRandomSeed}` : "";
    const response = await this.getCommands.getCommon(
      `getSongs?page=${nextPage}&limit=${pagination.limit}${artistParam}${randomParam}`
    );

    if (response?.items) {
      this.setState((prevState: any) => ({
        songList: [...prevState.songList, ...response.items],
        songPagination: {
          ...pagination,
          page: nextPage,
          hasMore: (nextPage * pagination.limit) < response.total,
        },
      }));
    }
  },

  async shuffleAlbums() {
    currentRandomSeed = Math.floor(Math.random() * 1000000);
    const PAGE_SIZE = 100;
    const listType = this.state.listType;

    if (listType === "Songs") {
      const response = await this.getCommands.getCommon(`getSongs?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`);
      if (response?.items) {
        this.setState({
          songList: response.items,
          songPagination: buildPagination(response),
        });
      }
    } else {
      const [artistResponse, albumResponse] = await Promise.all([
        listType === "Artists"
          ? this.getCommands.getCommon(`getArtists?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`)
          : Promise.resolve(null),
        listType !== "Artists"
          ? this.getCommands.getCommon(`getAlbums?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`)
          : Promise.resolve(null),
      ]);

      const newState: any = {};
      if (artistResponse?.items) {
        newState.artistList = artistResponse.items;
        newState.artistPagination = buildPagination(artistResponse);
      }
      if (albumResponse?.items) {
        newState.albumList = albumResponse.items;
        newState.albumPagination = buildPagination(albumResponse);
      }
      if (Object.keys(newState).length > 0) {
        this.setState(newState);
      }
    }
  },

  async filterSongsByArtist(artist: string | null) {
    const PAGE_SIZE = 100;
    const artistParam = artist ? `&artist=${encodeURIComponent(artist)}` : "";
    const response = await this.getCommands.getCommon(
      `getSongs?page=1&limit=${PAGE_SIZE}${artistParam}`
    );

    if (response?.items) {
      this.setState({
        songList: response.items,
        songPagination: buildPagination(response),
      });
    }
  },

  async initPage() {
    // Only load music data if on TierList or Grid pages
    const pathname = this.props.location?.pathname || "";
    const needsMusicData = pathname === "/TierList" || pathname === "/Grid" || pathname.startsWith("/tier-list");
    let artistList = this.state.artistList;
    let albumList = this.state.albumList;
    let albumPagination = this.state.albumPagination;
    let artistPagination = this.state.artistPagination;

    if (needsMusicData) {
      const PAGE_SIZE = 100;
      const initialArtist = this.state.initialGroupByValue;
      const listType = this.state.listType;

      if (listType === "Year") {
        // Year mode: fetch all albums for that year directly (no pagination)
        const year = this.state.defaultYear || "2026";
        const [artistResponse, albumResponse] = await Promise.all([
          this.getCommands.getCommon(`getArtists?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`),
          this.getCommands.getCommon(`getAlbums?year=${encodeURIComponent(year)}`),
        ]);

        artistList = artistResponse?.items || artistResponse;
        const albums = albumResponse?.items || albumResponse;
        albumList = Array.isArray(albums) ? albums : [];
        albumPagination = null;
        artistPagination = buildPagination(artistResponse);
      } else {
        const isFilterable = listType === "Albums" || listType === "Songs";
        const artistParam = (initialArtist && initialArtist !== "All" && isFilterable)
          ? `&artist=${encodeURIComponent(initialArtist)}`
          : "";

        const randomParam = !artistParam ? `&random=true&randomSeed=${currentRandomSeed}` : "";
        const [artistResponse, albumResponse] = await Promise.all([
          this.getCommands.getCommon(`getArtists?page=1&limit=${PAGE_SIZE}&random=true&randomSeed=${currentRandomSeed}`),
          this.getCommands.getCommon(`getAlbums?page=1&limit=${PAGE_SIZE}${artistParam}${randomParam}`),
        ]);

        artistList = artistResponse?.items || artistResponse;
        albumList = albumResponse?.items || albumResponse;
        albumPagination = buildPagination(albumResponse);
        artistPagination = buildPagination(artistResponse);
      }
    }

    const notificationList = await this.getCommands.getCommon("getNotifications");

    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", "en");
      this.setState({ language: "en" });
    }

    // Only load user-specific data if authenticated
    if (isAuthenticated()) {
      // Restore user object from JWT if not already set
      if (!this.state.user) {
        try {
          const meResponse = await authFetch(`${BASE_API_URL}/api/auth/me`);
          if (meResponse.ok) {
            const userData = await meResponse.json();
            this.setState({ user: userData, isAuthenticated: true });
          } else {
            clearToken();
            this.setState({ user: null, isAuthenticated: false, artistList, albumList, notificationList, albumPagination, artistPagination });
            return;
          }
        } catch {
          clearToken();
          this.setState({ user: null, isAuthenticated: false, artistList, albumList, notificationList, albumPagination, artistPagination });
          return;
        }
      }

      this.mainFunctions.loadCustomItems();

      const currentDate = moment().format("MM-DD-YYYY h:mm:ss a");
      let userProfiles = await this.getCommands.getCommon(
        `getUserInfo?currentTime=${currentDate}`
      );

      if (Object.keys(userProfiles).length === 0) {
        clearToken();
        this.setState({ user: null, isAuthenticated: false });
      } else {
        if (userProfiles) {
          const splitArrays = ["notifications_read"];
          splitArrays.forEach((item) => {
            if (userProfiles[item] !== undefined && userProfiles[item] !== null && userProfiles[item] !== "") {
              userProfiles[item] = userProfiles[item].split(",").map((val) => {
                if (val !== "") {
                  return parseInt(val, 10);
                }
              });
            } else {
              userProfiles[item] = [];
            }
          });

          this.setState(
            {
              userProfileInfo: userProfiles,
              artistList,
              albumList,
              notificationList,
              albumPagination,
              artistPagination,
            },
            () => {
              const siteNotifications = this.notificationHandler.generateNotificationList();
              this.setState({ siteNotifications });
            }
          );
          return;
        }
      }
    }

    // For guests or if user data failed, just set public data
    this.setState({ artistList, albumList, notificationList, albumPagination, artistPagination }, () => {
      const siteNotifications = this.notificationHandler.generateNotificationList();
      this.setState({ siteNotifications });
    });
  },

  async handleGoogleSignIn(credential) {
    try {
      const response = await fetch(`${BASE_API_URL}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        console.error("Sign-in failed");
        return;
      }

      const data = await response.json();
      setToken(data.token);

      this.setState(
        {
          user: data.user,
          isAuthenticated: true,
          isGuest: false,
        },
        () => {
          this.mainFunctions.initPage();
        }
      );
    } catch (err) {
      console.error("Google sign-in error:", err);
    }
  },

  continueAsGuest() {
    fetch(`${BASE_API_URL}/api/trackEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "continue_as_guest",
        eventData: { timestamp: new Date().toISOString() },
      }),
    }).catch(() => { });

    this.setState({ isGuest: true, isAuthenticated: false, user: null }, () => {
      this.mainFunctions.initPage();
    });
  },

  handleSignOut() {
    clearToken();
    this.setState({
      user: null,
      isAuthenticated: false,
      isGuest: false,
      userProfileInfo: {},
      customItems: { Artists: [], Albums: [], Songs: [] },
    });
  },

  async refetchProfile() {
    let userProfiles = await this.getCommands.getCommon(`fetchProfile`);
    if (userProfiles) {
      this.setState({
        userProfileInfo: userProfiles,
      });
    }
  },

  async saveDisplaySettings(settings) {
    const updateProfileAlias = await this.updateCommands.updateAliasName(settings.aliasName);
    if (!updateProfileAlias.ok) {
      return !updateProfileAlias.ok;
    } else if (updateProfileAlias.ok) {
      if (this.state.language !== settings.language) {
        localStorage.setItem("language", settings.language);
        window.location.reload();
      } else {
        const { userProfileInfo } = this.state;
        userProfileInfo.alias_name = settings.aliasName;
        this.setState(
          {
            userProfileInfo,
          },
          () => {
            return false;
          }
        );
      }
    }
  },

  getProfilePicture(artist, path, image) {
    if (image && !image.includes("https")) {
      if (artist.includes("Kanye") && artist.includes("Ty")) {
        image = "https://musicrandomizer.com/Images/ProfilePictures/K/Kanye+Ty.jpg";
      } else {
        if (/[a-zA-Z]/.test(artist[0])) {
          image = `https://musicrandomizer.com/Images/ProfilePictures/${artist[0].toUpperCase()}/${path}.jpg`;
        } else {
          image = `https://musicrandomizer.com/Images/ProfilePictures/${path}.jpg`;
        }
      }
    }
    return image;
  },

  //
};
